import { Component, OnInit } from '@angular/core';
import { ButtonComponent } from '@ppl/ui';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzInputModule } from 'ng-zorro-antd/input';
import { BaseComponent } from '../../../../shared/utils/base.component';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { CommonModule } from '@angular/common';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { RADIO_BOOLEAN_YES_NO_OPTIONS, RADIO_YES_NO_OPTIONS } from '../../../../shared/constants/static-data.constants';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { SectionsService } from '../../../../services/sections.service';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { ActivatedRoute, Router } from '@angular/router';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { AccordionComponent } from '@ppl/ui';
import { ClaimsService } from '../../../../services/claims.service';
import { DateTimeFormatterService } from '../../../../shared/utils/date-time-formatter.service';
import { LookupValueItem } from '../../../../interfaces/lookup.interface';
import { County } from '../../../../interfaces/region-counties.interface';
import { performValidation } from '../../../../shared/utils/validation.util';
import { LoggerService } from '../../../../shared/services/logger.service';
import { UserService } from '../../../../services/user.service';
import { DropdownSelectComponent } from '@ppl/ui';
import { DatepickerComponent } from '@ppl/ui';
import { AllowListAuthStore } from '../../../../shared/authorization/allow-list-auth.store';
import { PAGE_CONSTANTS } from '../../../../shared/constants/page.constants';

@Component({
  selector: 'ppl-add-payable-provider-rates',
  standalone: true,
  imports: [
    ButtonComponent,
    NzFormModule,
    ReactiveFormsModule,
    FormsModule,
    NzCardModule,
    NzInputModule,
    NzToolTipModule,
    CommonModule,
    NzDatePickerModule,
    NzToolTipModule,
    NzSelectModule,
    NzButtonModule,
    NzCollapseModule,
    AccordionComponent,
    DropdownSelectComponent,
    DatepickerComponent
  ],
  templateUrl: './add-payable-provider-rates.component.html',
  styleUrl: './add-payable-provider-rates.component.css',
})
export class AddPayableProviderRatesComponent
  extends BaseComponent
  implements OnInit {
  participantProviderRates!: FormGroup;
  radioOptions = RADIO_YES_NO_OPTIONS;
  radioBooleanOptions = RADIO_BOOLEAN_YES_NO_OPTIONS;
  pageId = PAGE_CONSTANTS.PAYABLE_PROVIDER_RATES_GRID_PAGE;
  isReadOnly = false;
  pageTitle = 'Add Payable - Personal Assistant Rate';
  mode = 'edit';
  savedData: any = {};
  isNew = true;
  showDialog = false;
  showSearchResults = false;
  searchResultsList: any[] = [];
  serviceCodes: any;
  program: any;
  regionalCenters: any = [];
  counties: LookupValueItem[] = [];
  states: LookupValueItem[] = [];
  cities: LookupValueItem[] = []

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private sectionsService: SectionsService,
    private claimsService: ClaimsService,
    private dateTimeFormatterService: DateTimeFormatterService,
    private logger: LoggerService,
    private route: ActivatedRoute,
    private userService: UserService,
    private allowListStore: AllowListAuthStore
  ) {
    super();
    this.participantProviderRates = this.fb.group(
      {
        serviceCodeDetailId: new FormControl('', [Validators.required]),
        rate: new FormControl('', [Validators.required]),
        fromDate: new FormControl('', [Validators.required]),
        toDate: new FormControl(''),
        orgId: new FormControl('', [Validators.required]),
        stateName: new FormControl({ value: '', disabled: true }, [Validators.required]),
        countyName: new FormControl(''),
        cityName: new FormControl(''),
      });
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params: any) => {
      const action = params.get('action')!;
      this.pageTitle =
        action == 'update' ? 'Payable - Personal Assistant Rate Details' : this.pageTitle;
      this.isNew = action === 'add';
      this.mode = action == 'update' ? 'read' : 'edit';
    });

    this.sectionsService.setFlow('payablePARates');
    this.program = this.userService.getSelectedProgram();
    this.secData = this.sectionsService.getSectionData(
      this.program.program_code,
      'payroll'
    );
    const assocRatesData =
      this.claimsService.getFormData('association_rates');
    this.getLookupValues();
    this.savedData = assocRatesData;
    this.setPageAccess();
  }

  private setPageAccess() {
    const pageConfig = this.allowListStore.getConfig()?.pages[this.pageId];
    if (pageConfig) {
      const allowedActions = this.allowListStore.getAllowedActions(this.pageId);
      const allowedButtons = this.allowListStore.getAllowedButtons(this.pageId);
      this.isReadOnly = allowedActions.length === 0 && allowedButtons.length === 0;
    } else {
      this.isReadOnly = true;
    }
  }

  getLookupValues(): void {
    this.claimsService.getRegionalCenters(this.program.program_code).subscribe((data: any) => {
      this.regionalCenters = data.responsedata;
    });

    this.serviceCodes = this.lookupService.getTasLookupValue(
      'servicecodeDetailList'
    );
    this.states = this.lookupService.getLookupValue('STATE');
    const californiaState = this.states.find(state => state.lkpValueCode === 'CA');
    if (californiaState) {
      this.participantProviderRates.get('stateName')?.setValue(californiaState.lkpValueName);
      this.cities = this.lookupService.getLookupValue('City');
    }

    this.counties = this.lookupService.getLookupValue('County');
  }

  returnClicked() {
    this.router.navigate(['/home/payablePersonalAssistantRates']);
  }

  goBack() {
    this.router.navigate(['/home/payablePersonalAssistantRates']);
  }

  onEdit() {
    this.mode = 'edit';
    const tempFromDate = new Date(this.savedData.fromDate);
    this.participantProviderRates.patchValue({
      ...this.savedData,
      orgId: +this.savedData.orgId,
      fromDate: new Date(tempFromDate.getUTCFullYear(), tempFromDate.getUTCMonth(), tempFromDate.getUTCDate(), 0, 0, 0, 0),
      toDate: this.savedData.toDate
        ? new Date(new Date(this.savedData.toDate).getUTCFullYear(), new Date(this.savedData.toDate).getUTCMonth(), new Date(this.savedData.toDate).getUTCDate(), 0, 0, 0, 0)
        : null
    });

    this.participantProviderRates.get('serviceCodeDetailId')?.disable();
    this.participantProviderRates.get('orgId')?.disable();
    this.participantProviderRates.get('stateName')?.disable();
    this.participantProviderRates.get('cityName')?.disable();
    this.participantProviderRates.get('countyName')?.disable();
    this.participantProviderRates.get('rate')?.disable();
    this.participantProviderRates.get('fromDate')?.disable();
  }

  createPayload() {
    return {
      ...this.participantProviderRates.getRawValue(),
      fromDate: this.formatOptionalDate(this.participantProviderRates.value.fromDate),
      toDate: this.formatOptionalDate(this.participantProviderRates.value.toDate),
      programCode: this.program.program_code,
    };
  }

  onAdd() {
    if (this.participantProviderRates.valid) {
      const formValues = this.participantProviderRates.value;
      const payload = this.createPayload();
      this.claimsService
        .addPayablePARates(this.program.program_code, payload, true)
        .subscribe((data: any) => {
          if (data.status == 200) {
            this.goBack();
          }
        });
    }
  }

  getTasLookupName(lookupArr: any, valueCode: string): string | undefined {
    const lookup = lookupArr.find((gen: any) => {
      return gen.serviceCodeDetailId === valueCode;
    });
    return lookup ? lookup.code : undefined;
  }

  onCancel() {
    this.mode = 'read';
    this.validationErrors = {};
    this.participantProviderRates.reset();
  }

  onUpdate() {
    const assocRatesData = this.claimsService.getFormData('association_rates');
    const paBillRateId = this.userService.getSelectedId();
    const payload = {
      ...this.participantProviderRates.getRawValue(),
      fromDate: this.formatOptionalDate(this.participantProviderRates.get('fromDate')?.getRawValue()),
      toDate: this.formatOptionalDate(this.participantProviderRates.get('toDate')?.getRawValue())
    }
    this.claimsService.updatePayablePARates(this.program.program_code, payload, assocRatesData.payRateId).subscribe((data) => {
      if (data.status == 200) {
        this.claimsService.getPayableProviderRates(this.program.program_code, paBillRateId, false)
          .subscribe((data: any) => {
            this.savedData = data.responsedata;
          });
        this.mode = 'read';
      }
    })
  }

  viewerValueChange(value: string) {
    const viewerValue = [value][0];
  }

  disabledStartDate = (startValue: Date): boolean => {
    const endDate = this.participantProviderRates.get('toDate')?.value;
    if (!startValue || !endDate || !(endDate instanceof Date)) {
      return false;
    }
    return startValue.getTime() > endDate.getTime();
  };

  disabledEndDate = (endValue: Date): boolean => {
    const startDate = this.participantProviderRates.get('fromDate')?.value;
    if (!endValue || !startDate || !(startDate instanceof Date)) {
      return false;
    }
    return endValue.getTime() <= startDate.getTime();
  };

  private formatOptionalDate(value: string | Date | null | undefined): string | null {
    return value ? this.dateTimeFormatterService.formatToString(new Date(value)) : null;
  }

  add() {
    this.showDialog = true;
  }

  action(event: any): void {
    if (event.action === 'add') {
      this.showDialog = false;
      this.showSearchResults = false;
    }
  }
}
